import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Feedback } from "@/components/workflow/shared";
import { errorMessage } from "@/lib/api";
import { danas } from "@/lib/dan";
import { hasProcurement, procurement, type Order } from "@/lib/procurement";
import { useReceiptDifferences } from "./ReceiptDifferences";
import { appendReceiptAttachments } from "@/lib/receiptPhotos";
import { ReceiptDestination, type ReceiptDestinationValue } from "./ReceiptDestination";

export function OrderReceipt({ order, onSaved, itemId }: { order: Order; itemId?: string; onSaved: (message: string) => void }) {
  const { user } = useAuth();
  const receipt = useReceiptDifferences(order, itemId), { mode } = receipt;
  const [files, setFiles] = useState<File[]>([]), [reason, setReason] = useState("");
  const [note, setNote] = useState(""), [date, setDate] = useState(danas());
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [destination, setDestination] = useState<ReceiptDestinationValue>("gradiliste");
  const hasOzo = order.stavke.some(item => item.vrsta_ozo_id);
  const exception = hasProcurement(user, "update", "all");
  const attachmentReady = mode === "nista" || files.length > 0 || (exception && reason.trim().length > 0);
  const ready = attachmentReady && receipt.valid && !!date && (!receipt.noArrival || !!note.trim());
  async function submit() {
    if (!ready || busy) return;
    setBusy(true); setError("");
    try {
      const body = new FormData();
      receipt.append(body);
      body.set("datum", date); body.set("napomena", note.trim());
      body.set("odrediste", hasOzo ? destination : "gradiliste");
      if (files.length) appendReceiptAttachments(body, files);
      else if (exception) body.set("bez_otpremnice_razlog", reason.trim());
      const result = await procurement.receive(order.id, body);
      onSaved(result.stanje === "stiglo" ? "Sve stiglo. Primitak je spremljen." : mode === "nista" ? "Zabilježeno je da roba nije stigla." : "Primitak je spremljen. Preostala roba još se čeka.");
    } catch (cause) { setError(errorMessage(cause, "Primitak nije spremljen. Pokušajte ponovno.")); }
    finally { setBusy(false); }
  }
  return <form aria-label="Primi robu" className="avm-procurement order-receipt space-y-4" onSubmit={event => { event.preventDefault(); void submit(); }}>
    <Feedback error={error} />
    <fieldset disabled={busy} className="space-y-4">
      <legend className="sr-only">Primi robu</legend>
      {hasOzo && <ReceiptDestination value={destination} onChange={setDestination} disabled={busy} />}
      <label className="min-h-11 cursor-pointer rounded-lg border p-3 font-medium focus-within:ring-2">Fotografiraj otpremnicu<input className="sr-only" type="file" accept="image/*" capture="environment" multiple onChange={event => { setFiles(Array.from(event.target.files || [])); setReason(""); }} /></label>
      <label className="min-h-11 cursor-pointer rounded-lg border p-3 font-medium focus-within:ring-2">Odaberi fotografije ili PDF<input className="sr-only" type="file" accept="image/*,application/pdf" multiple onChange={event => { setFiles(Array.from(event.target.files || [])); setReason(""); }} /></label>
      {files.length > 0 && <p>{files.length} priloga: {files.map(file => file.name).join(", ")}</p>}
      {exception && !files.length && <label>Razlog primitka bez otpremnice<textarea value={reason} onChange={event => setReason(event.target.value)} /></label>}
      {!attachmentReady && <p>Otpremnica je obavezna za potvrdu primitka.</p>}
      {receipt.controls}
      <label>Datum primitka<input type="date" required value={date} onChange={event => setDate(event.target.value)} /></label>
      <label>Napomena{receipt.noArrival ? " (obavezno)" : ""}<textarea required={receipt.noArrival} value={note} onChange={event => setNote(event.target.value)} /></label>
      <Button type="submit" disabled={!ready || busy}>{busy ? "Spremanje…" : "Potvrdi primitak"}</Button>
    </fieldset>
  </form>;
}
